/**
 * ProjectDefaultsPanel — S4 #defaults
 * canonical: lines 2101–2119
 * Saves: default_payment_terms, default_retainage, auto_prelien_ca, auto_attach_waivers
 */
import { useState, useEffect } from 'react'
import { saveSettings, type CompanySettings } from '../../../api/settings'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const PAYMENT_TERMS = ['Due on receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60']

interface Props {
  settings: CompanySettings | null
  onSaved: (s: CompanySettings) => void
}

export default function ProjectDefaultsPanel({ settings, onSaved }: Props) {
  const [terms,       setTerms]       = useState('Due on receipt')
  const [retainage,   setRetainage]   = useState('5')
  const [prelienCA,   setPrelienCA]   = useState(true)
  const [autoWaivers, setAutoWaivers] = useState(true)
  const [status,      setStatus]      = useState<SaveStatus>('idle')
  const [errMsg,      setErrMsg]      = useState('')

  useEffect(() => {
    if (!settings) return
    setTerms(settings.default_payment_terms   ?? 'Due on receipt')
    setRetainage(String(settings.default_retainage ?? 5))
    setPrelienCA(settings.auto_prelien_ca     ?? true)
    setAutoWaivers(settings.auto_attach_waivers ?? true)
  }, [settings])

  async function handleSave() {
    const retNum = parseFloat(retainage)
    if (isNaN(retNum) || retNum < 0 || retNum > 100) {
      setErrMsg('Retainage must be 0–100%')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
      return
    }
    setStatus('saving')
    try {
      const updated = await saveSettings({
        default_payment_terms: terms,
        default_retainage:     retNum,
        auto_prelien_ca:       prelienCA,
        auto_attach_waivers:   autoWaivers,
      })
      onSaved(updated)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2500)
    } catch (e) {
      setErrMsg((e as Error).message || 'Save failed')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
    }
  }

  // Button label never shows raw API error — error appears below as separate text
  const saveLabel = status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save defaults'

  return (
    <div id="defaults" className="panel" data-testid="panel-defaults" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Project defaults</div>
        <span className="panel-st">Auto-applied to every new project</span>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Default payment terms</div>
          <div className="set-row-st">Auto-fills the contract terms field on new projects</div>
        </div>
        <div className="set-row-r">
          <select
            className="fld-i"
            style={{ width: 160, padding: '6px 10px' }}
            value={terms}
            onChange={e => setTerms(e.target.value)}
            data-testid="payment-terms-select"
          >
            {PAYMENT_TERMS.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Default retainage</div>
          <div className="set-row-st">Withheld each draw · applied to new projects</div>
        </div>
        <div className="set-row-r">
          <input
            className="fld-i mono"
            value={retainage}
            onChange={e => setRetainage(e.target.value.replace('%', ''))}
            onBlur={e => { const v = e.target.value.replace('%', ''); setRetainage(v) }}
            placeholder="10"
            style={{ width: 80, padding: '6px 10px', textAlign: 'right' }}
            data-testid="retainage-input"
          />
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>%</span>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Auto-prepare 20-day Pre Lien for CA jobs</div>
          <div className="set-row-st">
            Soren drafts it in Step 5 of the wizard so you can review before filing.
            Only applies to projects with jurisdiction = California.
          </div>
        </div>
        <div className="set-row-r">
          <button
            className={`toggle${prelienCA ? ' on' : ''}`}
            onClick={() => setPrelienCA(v => !v)}
            aria-pressed={prelienCA}
            aria-label="Auto-prepare 20-day Pre Lien for CA jobs"
            data-testid="toggle-prelien-ca"
          />
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Auto-attach conditional waivers</div>
          <div className="set-row-st">
            Adds conditional lien waivers to outgoing pay apps automatically.
            Sub must sign before payment releases.
          </div>
        </div>
        <div className="set-row-r">
          <button
            className={`toggle${autoWaivers ? ' on' : ''}`}
            onClick={() => setAutoWaivers(v => !v)}
            aria-pressed={autoWaivers}
            aria-label="Auto-attach conditional waivers"
            data-testid="toggle-auto-waivers"
          />
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <button
          className={`btn ${status === 'saved' ? 'b-gh' : 'b-dk'}`}
          disabled={status === 'saving'}
          onClick={handleSave}
          data-testid="save-defaults-btn"
        >
          {saveLabel}
        </button>
        {status === 'error' && (
          <p style={{ color: 'var(--red)', fontSize: 11, marginTop: 6, marginBottom: 0 }}
            data-testid="save-defaults-error">
            {errMsg === 'No token' ? 'Session expired — please refresh the page.' : errMsg || 'Save failed. Try again.'}
          </p>
        )}
      </div>
    </div>
  )
}
